import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { AvatarService } from '@core/avatar.service';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { Avatar } from '@models/avatar';
import { AvatarSlide } from '@models/avatar-slide';
import { ProfanityCheck } from '@models/profanity-check';
import { Quest } from '@models/quest';
import { NgbActiveModal, NgbCarousel, NgbCarouselConfig, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { from, of, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import {
  StudentQuestCompleteComponent
} from '../../products/product/student-quest-complete/student-quest-complete.component';
import { GradeType } from '../enums/grade-type';
import { ProductType } from '../enums/product-type';
import { MarkdownService } from '../markdown.service';

@Component({
  selector: 'zbp-student-avatar-story-modal',
  templateUrl: './student-avatar-story-modal.component.html',
  styleUrls: ['./student-avatar-story-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class StudentAvatarStoryModalComponent implements OnInit, AfterViewInit {
  @Input() productType: ProductType;
  @Input() gradeType: GradeType;
  @ViewChild('carousel', { static: true }) carousel: NgbCarousel;
  @ViewChild('nextButton') nextButton: ElementRef;

  avatarForm: FormGroup;
  slide: number = 0;
  modalRef: NgbModalRef;
  loading: boolean = false;
  profanityBlock: boolean = false;
  isSaving: boolean = false;

  slides: AvatarSlide[] = [
    {
      imageName: 'blobStory1.jpg',
      captionText: `Welcome to ZB Portal! You must be STUDENTFIRSTNAME. I have been expecting you.
      My name is **Tip**. My job is to help you on your quest.
      “What quest,” you ask? Why, the *quest of knowledge*!`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory2.jpg',
      captionText: `I can’t wait to show you around ZB Portal! There is so much to see and...
      *WHOA!* What in the world is that bright light?`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory3.jpg',
      captionText: `The light came from somewhere over here.
      Wait, is that someone sitting alone? We should see if they’re okay.`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory4.jpg',
      captionText: `Hey there, little friend. Where did you come from? What was that bright light?
      Do you  remember your home? Do  you have a name?`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory4.jpg',
      captionText: '…………',
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory5.jpg',
      captionText: `Why, this poor little blob *doesn’t have a name*!
      That’s terrible, isn’t it? Everyone should have a name. STUDENTFIRSTNAME, can you think of a nice name for our blob friend?`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      captionText: `# Give the lost blob a name. 
##### (You can change it later!)`,
      requiresInput: true,
      formControlName: 'name',
      requiresProfanityCheck: true,
    },
    {
      imageName: 'blobStory6.jpg',
      captionText: `BLOBNAME really loves its new name!
      STUDENTFIRSTNAME, you really made this little blob’s day!`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory7.jpg',
      captionText: `Whoa, now *this* is impressive! Our little blob friend can also change its color.
      What do you think, STUDENTFIRSTNAME? What color should our blob friend be?`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
    {
      captionText: `# What color should our blob friend be?

##### (You can change it later!)`,
      requiresInput: true,
      formControlName: 'color',
      requiresProfanityCheck: false,
    },
    {
      imageName: 'blobStory8.jpg',
      captionText: `Amazing! What an unexpected, but wonderful surprise this blob turned out to be.
      It looks like ZB Portal is its home from now on. You’ll come back to visit your blob friend often, won’t you?
      STUDENTFIRSTNAME, I think you and BLOBNAME are going to be the best of friends this year!`,
      requiresInput: false,
      requiresProfanityCheck: false,
    },
  ];

  constructor(
    public  activeModal: NgbActiveModal,
    private userService: UserService,
    private avatarService: AvatarService,
    private markdownService: MarkdownService,
    private carouselConfig: NgbCarouselConfig,
    private modalService: NgbModal,
    private toastr: ToastrService) {
    this.carouselConfig.showNavigationArrows = false;
    this.carouselConfig.showNavigationIndicators = false;
    this.carouselConfig.interval = 0;
  }

  get canProgress(): boolean {
    return this.slides[this.slide]
      && (!this.slides[this.slide].requiresInput || this.avatarForm.get(this.slides[this.slide].formControlName).valid);
  }

  ngOnInit(): void {
    this.avatarForm = new FormGroup({
      name: new FormControl('', this.checkTextOnFormSlidesValidator('name')),
      color: new FormControl('', this.checkTextOnFormSlidesValidator('color')),
    });
    this.nameListener();
  }

  ngAfterViewInit() {
    // Prevent NgbCarousel from handling right/left arrow keyboard events
    this.carousel.keyboard = false;
  }

  public nameListener(): void {
    this.avatarForm.get('name').valueChanges.pipe(
      debounceTime(1),
      distinctUntilChanged(),
      filter(() => this.slides[this.slide].requiresProfanityCheck && this.avatarForm.value.name.length > 1),
      tap(() => {
        this.profanityBlock = true; // make sure button stays disabled until http call return
      }),
      switchMap(() => this.avatarService.checkForProfanity(this.avatarForm.value.name)) // Switch to the HTTP request observable
    ).subscribe((res: ApiResponse<ProfanityCheck>) => {
      if (res.success) {
        if (this.canProgress) {
          this.profanityBlock = false;
        }
      } else {
        this.toastr.error(res.messages[0]);
      }
    }, (err) => {
      this.activeModal.dismiss(err);
    });
  }

  next() {
    if ((this.slide + 1) >= this.slides.length) {
      // Save avatar after pressing next on the last slide.
      this.isSaving = true;
      this.saveAvatar();
    } else if (!this.slides[this.slide].requiresInput
      || this.avatarForm.get(this.slides[this.slide].formControlName).valid) {
      this.slide++;
      this.avatarForm.updateValueAndValidity();
      this.carousel.next();
      this.loading = true;
      timer(800).subscribe(() => {
        this.loading = false;
      });
    }
  }

  renderSlideCaption(text: string): string {
    const replaced = text
      .replace('STUDENTFIRSTNAME', this.userService.user.firstName)
      .replace('BLOBNAME', this.avatarForm.value.name);

    return this.markdownService.render(replaced);
  }

  nextButtonDisabled(): boolean {
    if (this.loading || !this.canProgress || this.profanityBlock || this.isSaving) return true;
    return false;
  }

  private checkTextOnFormSlidesValidator(formItem: string): ValidatorFn {
    if (formItem === 'name') {
      const pattern = /^[a-z0-9A-Z ][a-z0-9A-Z ]+$/;
      return (control?: FormControl): ValidationErrors => (
        control?.value && !pattern.test(control.value)
          ? { invalidAvatarName: 'Special characters are not allowed in your blob\'s name.' }
          : null
      );
    }

    return (control: FormControl): ValidationErrors => (
      !control.value ? { emptyText: 'You must select' } : null
    );
  }

  private saveAvatar() {
    const { name, color } = this.avatarForm.value;
    this.avatarService.saveAvatar(name, color, this.productType, this.gradeType)
      .subscribe((res: ApiResponse<Avatar>) => {
        if (res.success) {
          this.activeModal.close(res.response);
          this.openQuestCompleteDialog(res.response.level.totalStarCount);
        } else {
          this.activeModal.dismiss(res.messages);
          this.isSaving = false;
        }
      }, (err) => {
        this.activeModal.dismiss(err);
        this.isSaving = false;
      });
  }

  private openQuestCompleteDialog(totalStarCount: number): void {
    this.modalRef = this.modalService.open(StudentQuestCompleteComponent, { size: 'lg' });
    const quest = new Quest();
    quest.description = 'Befriend the lost blob';
    quest.numberOfStarsToEarn = totalStarCount;
    this.modalRef.componentInstance.initialize([quest]);

    from(this.modalRef.result)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
